import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import ChevronDownIcon from '@/assets/chevron-down.svg';
import ChevronRightIcon from '@/assets/chevron-right.svg';
import SettingsIcon from '@/assets/settings.svg';
import WarningIcon from '@/assets/warning.svg';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useCall } from '@/hooks/use-call';
import { useCallTones } from '@/hooks/use-call-tones';
import { useConnectionQuality } from '@/hooks/use-connection-quality';
import { CallState } from '@/types/websocket-call-messages';
import CallActionButton from '../calling/call-action-button';
import ConnectionQualityIndicator from '../calling/connection-quality-indicator';
import DeviceSelectionPanel from '../calling/device-selection-panel';
import IceSettingsPanel from '../calling/ice-settings-panel';
import StatusChip from '../calling/status-chip';

type PlaceCallModalProps = {
  open?: boolean;
  setOpen: (open: boolean) => void;
  unitId: number;
};

/**
 * If you are extracting components, please put them in the `src/components/calling` directory.
 */

export default function PlaceCallModal({
  open = false,
  setOpen,
  unitId,
}: PlaceCallModalProps) {
  const {
    callState,
    canEditIceSelection,
    connectionIssue,
    connectionState,
    endCall,
    excludedIceUrls,
    hasIceServers,
    iceSelection,
    icePreflightState,
    isReadyToCall,
    isSwitchingDevices,
    mediaDevices,
    mediaError,
    peerConnection,
    placeCall,
    remoteVideoRef,
    switchDevices,
    toggleIceUrlExclusion,
    videoRef,
  } = useCall(unitId, open);

  const [iceSettingsOpen, setIceSettingsOpen] = useState(false);

  const isCallActive =
    callState === CallState.IN_PROGRESS || callState === CallState.ACCEPTED;
  const qualityStats = useConnectionQuality(peerConnection, isCallActive);
  useCallTones({
    isDialling: callState === CallState.PLACING,
    isRinging: callState === CallState.RINGING,
  });

  /**
   * Fire a toast on the rising edge of a new media error so the user
   * notices even if they are looking at the device selector rather than
   * the video tile. The ref tracks which error name we last toasted so
   * we don't re-fire on every render while the same error is showing.
   */
  const lastToastedErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (mediaError && mediaError.name !== lastToastedErrorRef.current) {
      toast.error(mediaError.title, { description: mediaError.message });
      lastToastedErrorRef.current = mediaError.name;
    }
    if (!mediaError) {
      lastToastedErrorRef.current = null;
    }
  }, [mediaError]);

  const lastToastedConnectionIssueRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      connectionIssue &&
      connectionIssue.title !== lastToastedConnectionIssueRef.current
    ) {
      toast.error(connectionIssue.title, {
        description: connectionIssue.message,
      });
      lastToastedConnectionIssueRef.current = connectionIssue.title;
    }
    if (!connectionIssue) {
      lastToastedConnectionIssueRef.current = null;
    }
  }, [connectionIssue]);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'place-call-modal');
      }}
      fallback={
        <Modal
          buttonItems={[
            <Button
              key="close"
              onClick={() => setOpen(false)}
              variant="secondary"
            >
              Close
            </Button>,
          ]}
          open={open}
          setOpen={setOpen}
          title="Place Call"
        >
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-lg font-semibold text-red-600">
              Something went wrong
            </p>
            <p className="mt-2 text-sm text-gray-500">
              An error occurred while loading the call interface. Please close
              and try again.
            </p>
          </div>
        </Modal>
      }
    >
      <Modal
        large
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => setOpen(false)}
            variant="secondary"
          >
            Close
          </Button>,
          callState === CallState.PLACING ||
          callState === CallState.RINGING ||
          callState === CallState.IN_PROGRESS ||
          callState === CallState.ACCEPTED ? (
            <Button key="hang-up" onClick={endCall} variant="secondary-warning">
              Hang Up
            </Button>
          ) : (
            <CallActionButton
              callState={callState}
              isReadyToCall={isReadyToCall}
              key="call-action"
              onCall={placeCall}
            />
          ),
        ].filter(Boolean)}
        open={open}
        setOpen={setOpen}
        title="Place Call"
      >
        {/* Connection Status */}
        <div className="mb-4 flex items-start gap-4">
          <StatusChip
            settled={
              connectionState === 'connected' || connectionState === 'error'
            }
            status={
              connectionState === 'connected'
                ? 'success'
                : connectionState === 'error'
                  ? 'error'
                  : 'neutral'
            }
            text={
              connectionState === 'connected'
                ? 'Connected to call server'
                : connectionState === 'error'
                  ? 'Failed to connect to call server'
                  : 'Connecting to call server...'
            }
          />
          <StatusChip
            settled={
              connectionState !== 'connected' || icePreflightState === 'ready'
            }
            status={
              connectionState !== 'connected'
                ? 'neutral'
                : icePreflightState === 'ready'
                  ? 'success'
                  : 'info'
            }
            text={
              connectionState !== 'connected'
                ? 'Waiting for connection...'
                : icePreflightState === 'ready'
                  ? 'Network route established'
                  : hasIceServers
                    ? 'Checking network route...'
                    : 'Establishing network route...'
            }
          />
          <StatusChip
            settled={
              callState === CallState.ENDED ||
              callState === CallState.IDLE ||
              callState === CallState.IN_PROGRESS ||
              callState === CallState.ACCEPTED
            }
            status={
              callState === CallState.IDLE
                ? 'neutral'
                : callState === CallState.PLACING
                  ? 'info'
                  : callState === CallState.RINGING
                    ? 'warning'
                    : callState === CallState.IN_PROGRESS ||
                        callState === CallState.ACCEPTED
                      ? 'success'
                      : callState === CallState.ENDED
                        ? 'neutral'
                        : 'error'
            }
            text={
              callState === CallState.IDLE
                ? 'Waiting to place call'
                : callState === CallState.PLACING
                  ? 'Placing call...'
                  : callState === CallState.RINGING
                    ? 'Ringing...'
                    : callState === CallState.IN_PROGRESS ||
                        callState === CallState.ACCEPTED
                      ? 'Call in progress'
                      : callState === CallState.ENDED
                        ? 'Call Ended'
                        : 'Error'
            }
          />
        </div>

        {connectionIssue && (
          <div
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
            role="alert"
          >
            <p className="font-semibold">{connectionIssue.title}</p>
            <p className="mt-1">{connectionIssue.message}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {connectionIssue.advice.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Video Streams */}
        <div className="my-4 grid flex-1 grid-cols-2 gap-4">
          <div className="relative">
            <video
              autoPlay
              muted
              playsInline
              className="aspect-video h-full w-full rounded-lg border object-cover"
              ref={videoRef}
            />
            <div className="absolute bottom-2 left-2 rounded bg-black bg-opacity-50 px-2 py-1 text-xs text-white">
              Your video
            </div>
            {mediaError && (
              /**
               * Simple red-screen indicator over the local video tile.
               * The detailed error message, advice, and retry button
               * live in the DeviceSelectionPanel below so we don't
               * duplicate the same information in two places.
               */
              <div
                aria-label={mediaError.title}
                className="absolute inset-0 flex items-center justify-center rounded-lg bg-red-600"
                role="alert"
              >
                <WarningIcon className="h-12 w-12 text-white" />
              </div>
            )}
            {callState === CallState.ENDED && (
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black bg-opacity-75">
                <div className="text-lg font-semibold text-white">
                  Call Ended
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <video
              autoPlay
              playsInline
              className="aspect-video h-full w-full rounded-lg border object-cover"
              ref={remoteVideoRef}
            />
            <div className="absolute bottom-2 left-2 flex items-center gap-2 rounded bg-black bg-opacity-50 px-2 py-1 text-xs text-white">
              Their Video
              <ConnectionQualityIndicator stats={qualityStats} />
            </div>
            {(callState === CallState.ENDED ||
              callState === CallState.RINGING) && (
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black bg-opacity-75">
                <div className="text-lg font-semibold text-white">
                  {callState === CallState.ENDED ? 'Call Ended' : 'Ringing'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Device Selection */}
        <div className="mb-4">
          <DeviceSelectionPanel
            audioDevices={mediaDevices.audioDevices}
            deviceError={mediaDevices.deviceError}
            hasDevicePermission={mediaDevices.hasDevicePermission}
            isCallActive={callState !== CallState.IDLE}
            isEnumerating={mediaDevices.isEnumerating || isSwitchingDevices}
            onEnumerateDevices={mediaDevices.enumerateDevices}
            onSelectAudioDevice={(deviceId) => {
              /**
               * useCall.switchDevices rethrows so async callers can
               * observe failure, but this fire-and-forget call site
               * already gets feedback via the toast + overlay. Swallow
               * the rejection here to avoid an unhandled promise.
               */
              switchDevices(undefined, deviceId).catch(() => {});
            }}
            onSelectVideoDevice={(deviceId) => {
              switchDevices(deviceId, undefined).catch(() => {});
            }}
            selectedAudioDevice={mediaDevices.selectedAudioDevice}
            selectedVideoDevice={mediaDevices.selectedVideoDevice}
            videoDevices={mediaDevices.videoDevices}
          />
        </div>

        {icePreflightState === 'ready' && iceSelection && (
          <div className="mb-4 rounded-lg border border-gray-200">
            <Button
              aria-expanded={iceSettingsOpen}
              aria-label="Advanced Configuration"
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
              onClick={() => setIceSettingsOpen((open) => !open)}
              type="button"
              variant="unstyled"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <SettingsIcon className="h-5 w-5 text-gray-500" />
                Advanced Configuration
              </span>
              {iceSettingsOpen ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-500" />
              )}
            </Button>
            {iceSettingsOpen && (
              <div className="border-t border-gray-200">
                <IceSettingsPanel
                  disabled={!canEditIceSelection}
                  excludedIceUrls={excludedIceUrls}
                  iceSelection={iceSelection}
                  onToggle={toggleIceUrlExclusion}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Sentry.ErrorBoundary>
  );
}
